// MAMORU - サーバーAPI クライアント
// 検知ヒットの文脈判別をサーバーに問い合わせる。

import type { Hit } from './types';

export type JudgeRequest = {
  text: string;
  candidates: Array<{ name: string; works: string[] }>;
};

export type JudgeResponseItem = {
  name: string;
  fictional_score: number;   // 0..1: 1ならフィクション文脈に近い
  noise_score: number;       // 0..1: 1なら一般語/異義語の文脈
  decision: 'red' | 'yellow' | 'green' | 'suppress';
};

export type JudgeResponse = {
  ok: boolean;
  results?: JudgeResponseItem[];
  error?: string;
};

// 開発時はvite.config.tsのproxyで /api → localhost:8787 に流す（worker:devモード時のみ有効）
// 本番（Vite build後）は Cloudflare Workers のURLを直接指定
const API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://mamoru-judge.studioveco.workers.dev/api';

export async function judge(req: JudgeRequest): Promise<JudgeResponse> {
  try {
    const res = await fetch(`${API_BASE}/judge`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    return (await res.json()) as JudgeResponse;
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

// === Visual Search (特徴ベース検出) ============================
export type VisualMatch = {
  id: string;
  score: number;       // 0..1 コサイン類似度
  name: string;
  works: string[];
};
export type VisualSearchResponse = {
  ok: boolean;
  matches?: VisualMatch[];
  error?: string;
};

export async function visualSearch(text: string, topK = 5): Promise<VisualSearchResponse> {
  try {
    const res = await fetch(`${API_BASE}/visual-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, topK }),
    });
    if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
    return (await res.json()) as VisualSearchResponse;
  } catch (e) {
    return { ok: false, error: String(e) };
  }
}

// クライアント側Hit と サーバー判定をマージしてレベル更新
// 戻り値: { kept: 抑制後のヒット, all: 抑制含む全件（ベクトル分析表示用） }
export function applyJudgement(
  hits: Hit[],
  judgement: JudgeResponseItem[],
): { kept: Hit[]; all: Hit[] } {
  const map = new Map(judgement.map((j) => [j.name, j]));
  const all: Hit[] = hits.map((h) => {
    const j = map.get(h.name);
    if (!j) return h;
    const vector = {
      fictional: j.fictional_score,
      noise: j.noise_score,
      decision: j.decision,
    };
    if (j.decision === 'suppress') {
      return { ...h, vector };
    }
    return {
      ...h,
      level: j.decision === 'green' ? 'green' : (j.decision === 'red' ? 'red' : 'yellow'),
      vector,
    };
  });
  const kept = all.filter((h) => h.vector?.decision !== 'suppress');
  return { kept, all };
}
